import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useFormContext, Controller } from 'react-hook-form';
import clsx from 'clsx';
import { makeStyles } from '@material-ui/core';
import { RootState } from '@modules/index';
import {
  RegisterRfqInput,
  CapaRfqPcbAssemblyMethodType,
  PcbAssemblyMethodInput
} from '@generated/graphql';
import { colorSet } from '@components/Provider';
import { CustomizedTypography } from '@components/Customized';
import * as assets from '@assets';
import FormInputLayout from '../Layout/FormInputLayout';
import { RequestInputCommonProps } from './types';

const AssemblyMethodControl: React.FC<RequestInputCommonProps> = ({
  error
}) => {
  const assemblyMethodDefaultValue = useSelector<
    RootState,
    PcbAssemblyMethodInput[]
  >((state) => state.request.pcb?.assemblyMethod ?? []);
  const { controlContainer } = useStyles();
  const { control } = useFormContext<RegisterRfqInput>();
  const [method, setMethod] = useState<CapaRfqPcbAssemblyMethodType[]>(
    assemblyMethodDefaultValue.map((method) => method.methodType) ?? []
  );

  const onChangeValue = (
    arr: CapaRfqPcbAssemblyMethodType[] | undefined = [],
    value: CapaRfqPcbAssemblyMethodType
  ) => {
    let result = [];
    if (arr.includes(value)) {
      result = arr.filter((el) => el !== value);
    } else {
      result = [...arr, value];
    }

    setMethod(result);
    return result.map((method) => ({ methodType: method }));
  };

  return (
    <FormInputLayout
      label="조립방식"
      required={true}
      extraLabel={'(중복선택 가능)'}
    >
      <Controller
        data-cy="rfq-pcb-assemblyMethod"
        name="pcb.assemblyMethod"
        control={control}
        rules={{
          required: '조립방식을 선택해주세요'
        }}
        defaultValue={assemblyMethodDefaultValue.map(
          (method) => method.methodType
        )}
        render={({ onChange }) => (
          <div className={controlContainer}>
            <div
              className={clsx([
                'switch',
                {
                  'selected': method.includes(CapaRfqPcbAssemblyMethodType.Smt)
                }
              ])}
              onClick={(e) => {
                const result = onChangeValue(
                  method,
                  CapaRfqPcbAssemblyMethodType.Smt
                );
                onChange(result);
              }}
            >
              <img
                src={assets.PCBAssembleMethodSMTIcon}
                width={40}
                height={40}
                alt="조립방식 : SMT"
              />
              <CustomizedTypography
                variant="body1"
                fontWeight="500"
                color="textPrimary"
              >
                SMT
              </CustomizedTypography>
            </div>
            <div
              className={clsx([
                'switch',
                {
                  selected: method.includes(
                    CapaRfqPcbAssemblyMethodType.Soldering
                  )
                }
              ])}
              onClick={(e) => {
                const result = onChangeValue(
                  method,
                  CapaRfqPcbAssemblyMethodType.Soldering
                );
                onChange(result);
              }}
            >
              <img
                src={assets.PCBAssembleMethodSolderingIcon}
                width={48}
                height={40}
                alt="조립방식 : 수땜"
              />
              <CustomizedTypography
                variant="body1"
                fontWeight="500"
                color="textPrimary"
              >
                수땜
              </CustomizedTypography>
            </div>
          </div>
        )}
      />

      {error && (
        <CustomizedTypography variant="caption" color="error">
          {error}
        </CustomizedTypography>
      )}
    </FormInputLayout>
  );
};

export default AssemblyMethodControl;

const useStyles = makeStyles((theme) => ({
  controlContainer: {
    display: 'flex',
    gap: '16px',
    [theme.breakpoints.down('sm')]: {
      gap: '8px'
    },
    '& .switch': {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      padding: '20px 18px',
      borderRadius: '12px',
      border: `1px solid ${colorSet.gray400}`,
      backgroundColor: colorSet.gray000,
      gap: '12px',
      '&:hover': {
        cursor: 'pointer'
      },
      '&.selected': {
        border: `2px solid ${colorSet.gray900}`,
        padding: '19px 17px',
        backgroundColor: colorSet.gray100
      },
      [theme.breakpoints.up('md')]: {
        '&:hover, &.selected': {
          border: `2px solid ${colorSet.gray900}`,
          padding: '19px 17px'
        }
      }
    }
  }
}));
